import React from 'react';
import { Form, Formik } from 'formik';
import { isEmpty } from 'lodash';
import { FormElement, Label } from '@dashboard/components/Form';
import FormSubmit from '@dashboard/components/Formik/components/FormSubmit';
import { Checkbox, Radio } from '@dashboard/components/Formik';
import FormError, { FormErrorBase } from '@dashboard/components/Formik/components/FormError';
import Link from '@dashboard/components/Link';
import { transformApiErrorToFormik } from '@dashboard/services/form';
import CanUseFeature from '@dashboard/components/CanUseFeature';
import Features from '@dashboard/enums/features';
import Button from '@dashboard/components/Button';
import usePage from '@dashboard/hooks/usePage';
import Callout from '@dashboard/components/Callout';
import getStripeDashboardUrl from '@dashboard/lib/stripe/get-stripe-dashboard-url';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import checkIsConnectedToStripe from '@dashboard/services/seller/checkIsConnectedToStripe';
import Description from '@dashboard/components/Description';

function getPaymentMethod(paymentMethods) {
  // if card and agpay are enabled, return 'card_agpay'
  if (paymentMethods.card?.enabled && paymentMethods.agpay?.enabled) {
    return 'card_agpay';
  }

  // if card is enabled, return 'card'
  if (paymentMethods.card?.enabled) {
    return 'card';
  }

  // if muliple is enabled, return 'multiple'
  if (paymentMethods.multiple?.enabled) {
    return 'multiple';
  }
}

function parsePaymentMethods(paymentMethod, mode) {
  switch (paymentMethod) {
    case 'card_agpay':
      return {
        card: { enabled: true },
        agpay: { enabled: true, mode },
        multiple: { enabled: false },
      };
    case 'card':
      return {
        card: { enabled: true },
        agpay: { enabled: false, mode },
        multiple: { enabled: false },
      };
    case 'multiple':
      return {
        card: { enabled: false },
        agpay: { enabled: false, mode },
        multiple: { enabled: true },
      };
    default:
      return {};
  }
}

const PaymentMethodsForm = () => {
  const { seller, isAdminLoggedInAsSeller } = useAuth();
  const { page, updatePage } = usePage();

  return (
    <Formik
      enableReinitialize
      initialValues={{
        paymentMethod: getPaymentMethod(page.paymentMethods.stripe),
        mode: page.paymentMethods.stripe.agpay?.mode || 'default',
        savePaymentMethod: page.savePaymentMethod || false,
      }}
      onSubmit={async (values, actions) => {
        if (values.paymentMethod === 'multiple') {
          if (!checkIsConnectedToStripe(seller)) {
            actions.setStatus('First connect to Stripe to enable multiple payment methods');
            return;
          }
        }

        actions.setStatus(undefined);

        const paymentMethods = parsePaymentMethods(values.paymentMethod, values.mode);

        const { data, error } = await updatePage.mutateAsync({
          paymentMethods: {
            stripe: paymentMethods,
          },
          savePaymentMethod: values.savePaymentMethod,
        });

        if (data) {
          actions.setStatus('');
        } else {
          const { status, errors } = transformApiErrorToFormik(error);

          if (!isEmpty(errors)) {
            actions.setErrors(errors);
          } else {
            actions.setStatus(status);
          }
        }
      }}
    >
      {({ status, dirty, handleSubmit, values }) => (
        <Form onSubmit={handleSubmit}>
          <FormElement>
            <Label>Payment methods</Label>
            <Radio.Group>
              <Radio.Wrapper>
                <Radio.Input name="paymentMethod" value="card_agpay" id="card_agpay" />
                <Radio.Label htmlFor="card_agpay">
                  Accept payments with card & Apple / Google Pay
                </Radio.Label>
              </Radio.Wrapper>
              <Radio.Wrapper>
                <Radio.Input name="paymentMethod" value="card" id="card" />
                <Radio.Label htmlFor="card">Accept payments with card</Radio.Label>
              </Radio.Wrapper>

              <>
                <CanUseFeature feature={Features.MULTIPLE_PAYMENT_METHODS}>
                  <Radio.Wrapper>
                    <Radio.Input name="paymentMethod" value="multiple" id="multiple" />
                    <Radio.Label htmlFor="multiple">Accept multiple payment methods</Radio.Label>
                  </Radio.Wrapper>
                </CanUseFeature>
              </>
            </Radio.Group>

            <FormError name="paymentMethod" />
          </FormElement>

          {values.paymentMethod === 'multiple' && (
            <FormElement>
              <Label>Multiple payment methods</Label>
              <Description>
                Accept Apple Pay, Google Pay, buy now pay later services, bank debits, local payment
                methods & more.{' '}
                <Link href="/help/articles/accept-multiple-payment-methods" target="_blank">
                  Learn more
                </Link>
              </Description>
              <Description>
                <Button
                  href={getStripeDashboardUrl(
                    `/settings/payment_methods?platform_id=${process.env.REACT_APP_STRIPE_PLATFORM_ID}`,
                    seller,
                    isAdminLoggedInAsSeller,
                  )}
                  target="_blank"
                  appearance="secondary"
                  className="mt-2"
                  external
                >
                  Enable payment methods in Stripe
                </Button>
              </Description>
            </FormElement>
          )}

          {values.paymentMethod === 'card_agpay' && (
            <FormElement>
              <Label htmlFor="mode">Apple Pay / Google Pay mode</Label>

              <Radio.Group>
                <Radio.Wrapper>
                  <Radio.Input name="mode" value="default" id="default" />
                  <Radio.Label htmlFor="default">Use as payment method</Radio.Label>
                </Radio.Wrapper>

                <Radio.Wrapper>
                  <Radio.Input name="mode" value="express" id="express" />
                  <Radio.Label htmlFor="express">Use as Express Checkout </Radio.Label>
                </Radio.Wrapper>
              </Radio.Group>

              <FormError name="mode" />
            </FormElement>
          )}

          {values.paymentMethod === 'card_agpay' && values.mode === 'express' && (
            <FormElement>
              <Label>Apple Pay / Google Pay Express Checkout</Label>
              <Description>
                Automatically collects name and email, but allows your customer to skip other
                checkout fields.{' '}
                <Link
                  to="/help/articles/accept-payments-with-card-apple-pay-google-pay#comparing-additional-payment-method-vs-express-checkout"
                  target="_blank"
                >
                  Learn more
                </Link>
              </Description>
            </FormElement>
          )}

          {(values.paymentMethod === 'card_agpay' || values.paymentMethod === 'multiple') && (
            <Callout variant="info">
              <Callout.Heading>Note</Callout.Heading>
              <Callout.Content>
                To use Apple Pay / Google Pay on embed and pop-up checkouts on your site, you need
                to{' '}
                <Link href="/settings/payments#payment-method-domains">register your domain</Link>
              </Callout.Content>
            </Callout>
          )}

          <FormElement>
            <Checkbox.Wrapper>
              <Checkbox name="savePaymentMethod" id="savePaymentMethod" />
              <Checkbox.Label
                htmlFor="savePaymentMethod"
                tooltip="Automatically saves the customer's payment method to your Stripe account when they make a one-time payment. This lets you charge them again in the future if needed. Payment methods for subscriptions are always saved."
              >
                Save payment method on file when a one-time payment is made
              </Checkbox.Label>
            </Checkbox.Wrapper>
          </FormElement>

          {status && <FormErrorBase>{status}</FormErrorBase>}

          <FormSubmit>
            <FormSubmit.Button>Save</FormSubmit.Button>
            <FormSubmit.State visible={!!dirty} />
          </FormSubmit>
        </Form>
      )}
    </Formik>
  );
};

export default PaymentMethodsForm;
